import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { BottomTab } from "@/components/BottomTab";

const UPDATED = "Giugno 2026";

const Section = ({ title, children }) => (
  <div className="mt-6">
    <h2 className="font-black text-lg text-onsurface mb-2">{title}</h2>
    <div className="space-y-2 text-[14px] leading-relaxed text-onsurface">{children}</div>
  </div>
);

export default function Privacy() {
  const nav = useNavigate();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.get("/event").then((r) => setEvent(r.data)).catch(() => {});
  }, []);

  const email = event?.contact_email || "themadbar26@gmail.com";
  const ig = event?.contact_instagram || "the_mad_bar";

  return (
    <div className="min-h-screen pb-24 bg-surface" data-testid="privacy-page">
      <header className="bg-black px-4 pb-5 flex items-center gap-3" style={{ paddingTop: "calc(var(--safe-top) + 16px)" }}>
        <button data-testid="privacy-back-btn" onClick={() => nav("/")} className="text-white">
          <ChevronLeft size={32} strokeWidth={2.5} />
        </button>
        <ShieldCheck className="text-brand" size={26} />
        <h1 className="text-2xl font-black text-white">Privacy & Cookie</h1>
      </header>

      <div className="px-5 pt-5">
        <p className="text-onsurface3 text-[13px] font-secondary">Ultimo aggiornamento: {UPDATED}</p>

        <div className="bg-surface2 rounded-3xl p-5 mt-4">
          <p className="text-[14px] leading-relaxed text-onsurface">
            La presente informativa descrive come vengono trattati i dati personali degli utenti che
            utilizzano l'applicazione web <span className="font-black text-onsurface">The Mad Bar</span> (di
            seguito "l'App"), in conformità al Regolamento (UE) 2016/679 ("GDPR") e al D.lgs. 196/2003
            come modificato dal D.lgs. 101/2018 ("Codice Privacy").
          </p>
        </div>

        <h2 className="font-black text-2xl text-onsurface mt-8">Privacy Policy</h2>

        <Section title="1. Titolari del trattamento">
          <p>
            Il trattamento dei dati personali effettuato tramite l'App relativa all'evento{" "}
            <span className="font-bold text-onsurface">The Mad Bar</span> è svolto in
            contitolarità (art. 26 GDPR), in qualità di organizzatori dell'evento, da:
          </p>
          <p>• <span className="font-bold text-onsurface">Filippo Bergia Boccardo</span> — C.F. BRGFPP00M07D969M</p>
          <p>• <span className="font-bold text-onsurface">Gianluca Bruzzone</span> — C.F. BRZGLC89M12D969Z</p>
          <p>di seguito, congiuntamente, i "Titolari".</p>
          <p>
            I Titolari hanno definito in modo trasparente le rispettive responsabilità in merito al
            trattamento; il punto di contatto unico per gli interessati è indicato di seguito.
          </p>
          <p>
            Per esercitare i propri diritti o per qualsiasi richiesta relativa ai dati personali è
            possibile scrivere a{" "}
            <a href={`mailto:${email}`} className="text-brand font-bold break-all">{email}</a>{" "}
            oppure contattare il profilo Instagram <span className="text-brand font-bold">@{ig}</span>.
          </p>
          <p className="text-onsurface3 text-[12px]">
            Nota: l'App non gestisce la vendita di biglietti né i pagamenti. L'acquisto del biglietto e
            l'iscrizione avvengono su una piattaforma esterna gestita da <span className="font-bold">HOLDDAT
            A.S.D.</span> (Via Cassanello 4R, 16156 Genova Pegli — C.F. 95214220105), che agisce quale
            autonomo titolare del trattamento per i dati raccolti in fase di acquisto/iscrizione, secondo
            la propria informativa privacy.
          </p>
        </Section>

        <Section title="2. Dati personali trattati">
          <p>L'App tratta le seguenti categorie di dati:</p>
          <p className="font-bold text-onsurface">a) Dati inviati tramite il modulo Contatti</p>
          <p>Nome, indirizzo email e testo del messaggio che l'utente inserisce volontariamente per
            contattare lo staff.</p>
          <p className="font-bold text-onsurface">b) Dati degli atleti partecipanti</p>
          <p>Nome, eventuale nome utente Instagram (se fornito) e dati di gara (semi, tempi, giri,
            reps, punteggi, orari, corsia). Tali dati sono inseriti e gestiti esclusivamente dallo
            staff a fini sportivi e vengono mostrati nelle classifiche pubbliche dell'evento.</p>
          <p className="font-bold text-onsurface">c) Dati tecnici</p>
          <p>L'App non utilizza sistemi di analisi statistica né di profilazione. Vengono utilizzati
            unicamente dati di archiviazione locale tecnici (vedi Cookie Policy).</p>
        </Section>

        <Section title="3. Finalità e base giuridica">
          <p>• <span className="font-bold text-onsurface">Riscontro alle richieste di contatto</span> —
            base giuridica: consenso e/o esecuzione di misure precontrattuali (art. 6.1.a/b GDPR).</p>
          <p>• <span className="font-bold text-onsurface">Organizzazione e svolgimento della
            competizione</span> e pubblicazione delle classifiche — base giuridica: esecuzione di un
            contratto/iscrizione e legittimo interesse dell'organizzazione (art. 6.1.b/f GDPR).</p>
          <p>• <span className="font-bold text-onsurface">Funzionamento tecnico dell'App</span> (area
            staff protetta) — base giuridica: legittimo interesse (art. 6.1.f GDPR).</p>
        </Section>

        <Section title="4. Modalità del trattamento e destinatari">
          <p>I dati sono trattati con strumenti informatici e conservati su database (MongoDB) ospitato
            presso l'infrastruttura di hosting dell'App, con misure di sicurezza adeguate.</p>
          <p>I messaggi inviati dal modulo Contatti vengono recapitati via email allo staff tramite un
            fornitore terzo di servizi di invio email (Resend), che agisce come responsabile del
            trattamento. Tale invio può comportare un trasferimento di dati verso Paesi terzi (extra
            SEE): in tal caso il trasferimento avviene sulla base di garanzie adeguate (es. Clausole
            Contrattuali Standard della Commissione UE).</p>
          <p>I dati non sono oggetto di diffusione ulteriore né di vendita a terzi.</p>
        </Section>

        <Section title="5. Periodo di conservazione">
          <p>I messaggi di contatto sono conservati per il tempo necessario a evadere la richiesta e per
            un periodo massimo di 24 mesi. I dati di gara sono conservati per finalità di archivio
            storico dell'evento. L'interessato può richiederne in ogni momento la cancellazione.</p>
        </Section>

        <Section title="6. Diritti dell'interessato">
          <p>L'utente può in ogni momento esercitare i diritti previsti dagli artt. 15-22 GDPR: accesso,
            rettifica, cancellazione ("diritto all'oblio"), limitazione, opposizione al trattamento e
            portabilità dei dati, nonché revoca del consenso.</p>
          <p>Per esercitare tali diritti è sufficiente scrivere a{" "}
            <a href={`mailto:${email}`} className="text-brand font-bold break-all">{email}</a>.</p>
          <p>È inoltre possibile proporre reclamo all'Autorità Garante per la protezione dei dati
            personali (www.garanteprivacy.it).</p>
        </Section>

        <Section title="7. Minori">
          <p>L'App non è destinata alla raccolta consapevole di dati di minori di 14 anni senza il
            consenso di chi esercita la responsabilità genitoriale. In caso di iscrizione di atleti
            minorenni, i relativi dati sono trattati sotto la responsabilità di chi effettua
            l'iscrizione.</p>
        </Section>

        <h2 className="font-black text-2xl text-onsurface mt-10">Cookie Policy</h2>

        <Section title="8. Cookie e archiviazione locale">
          <p>L'App <span className="font-bold text-onsurface">non utilizza cookie di profilazione,
            pubblicitari o di analisi statistica</span> e non condivide dati con piattaforme di
            tracciamento di terze parti.</p>
          <p>Viene utilizzata esclusivamente l'<span className="font-bold text-onsurface">archiviazione
            locale tecnica</span> (localStorage) del browser, necessaria al funzionamento, per il
            seguente scopo:</p>
          <p>• <span className="font-bold text-onsurface">Token area staff</span> (chiave
            <span className="font-mono text-[13px]"> madbar_admin_token</span>): consente ai soli
            amministratori di rimanere autenticati nell'area di gestione. Non viene impostato per gli
            utenti pubblici.</p>
          <p>Trattandosi di storage tecnico strettamente necessario, ai sensi della normativa vigente
            non è richiesto il consenso preventivo.</p>
        </Section>

        <Section title="9. Come gestire l'archiviazione locale">
          <p>L'utente può eliminare in qualsiasi momento i dati di archiviazione locale svuotando i dati
            del sito dalle impostazioni del proprio browser. La rimozione del token comporterà
            semplicemente la disconnessione dall'eventuale area staff.</p>
        </Section>

        <Section title="10. Modifiche alla presente informativa">
          <p>Il Titolare si riserva di aggiornare la presente informativa. Eventuali modifiche saranno
            pubblicate su questa pagina con l'indicazione della data di ultimo aggiornamento.</p>
        </Section>

        <div className="h-6" />
      </div>

      <BottomTab />
    </div>
  );
}
